import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';
import { useAppStore } from '@/stores/appStore';
import { useFloorPlanContext } from '../FloorPlanProvider';
import { useEffect, useRef } from 'react';

interface CircuitDrawerV2Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isMobile?: boolean;
  newCircuitName: string;
  setNewCircuitName: (name: string) => void;
  handleCreateCircuitWithFixtures: () => Promise<void>;
  exitCircuitMode: () => void;
  isPending?: boolean;
}

export function CircuitDrawerV2({
  open,
  onOpenChange,
  isMobile = false,
  newCircuitName,
  setNewCircuitName,
  handleCreateCircuitWithFixtures,
  exitCircuitMode,
  isPending = false,
}: CircuitDrawerV2Props) {
  const selectedFixtures = useAppStore(state => state.selection.fixtures);
  const clearEntitySelection = useAppStore(state => state.clearEntitySelection);
  const { data } = useFloorPlanContext();
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus the input when drawer opens (desktop only)
  useEffect(() => {
    // Check if this is a touch device
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    if (open) {
      if (isTouchDevice) {
        // On touch devices, ensure nothing gets focused
        setTimeout(() => {
          if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
          }
        }, 50);
      } else if (inputRef.current) {
        // Desktop only - focus the input and keep it focused
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
      }
    }
  }, [open]);

  // Keep focus on desktop when fixtures selection changes
  useEffect(() => {
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    // On desktop, refocus the input whenever selection changes
    if (!isTouchDevice && open && inputRef.current && document.activeElement !== inputRef.current) {
      // Small delay to ensure any other focus changes have completed
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    }
  }, [selectedFixtures.size, open]);

  // Content rendering function
  const renderContent = () => (
    <div className="py-4">
      <div className="space-y-4">
        {/* Instructions */}
        <div className="text-sm text-muted-foreground bg-blue-50 p-3 rounded-md">
          <p className="font-medium text-blue-900 mb-1">Selection Tips:</p>
          <ul className="list-disc list-inside space-y-1 text-blue-800">
            <li>Click fixtures to toggle selection</li>
            <li>Drag to select multiple fixtures - new selections are added to existing ones</li>
            <li>Press ESC or click the arrow tool to exit load creation mode</li>
          </ul>
        </div>

        <div>
          <p className="text-sm font-medium mb-2">Selected Fixtures</p>
          <div className="space-y-2">
            <Badge variant="secondary">{selectedFixtures.size} fixtures selected</Badge>

            {/* Show warning for assigned fixtures */}
            {Array.from(selectedFixtures).some(
              id => data.fixtures?.find(f => f.id === id)?.circuit_id,
            ) && (
              <div className="rounded-md bg-yellow-50 p-3">
                <div className="flex">
                  <div className="text-yellow-800">
                    <p className="text-sm">
                      Some selected fixtures are already assigned to other loads. Creating this load
                      will remove them from their current loads.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <div>
          <p className="text-sm font-medium mb-2">Load Name</p>
          <Input
            ref={inputRef}
            value={newCircuitName}
            onChange={e => setNewCircuitName(e.target.value)}
            placeholder="Enter load name"
            autoFocus={false}
            onFocus={e => {
              // Check if this is a touch device
              const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
              if (isTouchDevice) {
                // Only blur if focus wasn't triggered by direct user interaction
                if (!e.isTrusted) {
                  e.currentTarget.blur();
                }
              }
              // On desktop, maintain focus
            }}
            onKeyDown={async e => {
              e.stopPropagation(); // Prevent space-to-pan interference
              if (e.key === 'Enter') {
                e.preventDefault();
                await handleCreateCircuitWithFixtures();
                // Re-focus the input after creating circuit (desktop only)
                const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
                if (!isTouchDevice) {
                  setTimeout(() => {
                    inputRef.current?.focus();
                  }, 100);
                }
              }
            }}
            onKeyUp={e => e.stopPropagation()}
          />
        </div>

        <div className="flex justify-end space-x-2">
          <Button
            variant="outline"
            onClick={() => {
              // Clear selections
              clearEntitySelection('fixture');
              setNewCircuitName('');
            }}
          >
            Clear Selection
          </Button>
          <Button
            onClick={async () => {
              await handleCreateCircuitWithFixtures();
              // Re-focus the input after creating circuit
              setTimeout(() => {
                inputRef.current?.focus();
              }, 100);
            }}
            disabled={selectedFixtures.size === 0 || !newCircuitName?.trim()}
          >
            Create Load
          </Button>
        </div>
      </div>
    </div>
  );

  // Responsive rendering
  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent>
          <DrawerHeader className="relative">
            <DrawerTitle>Create New Load</DrawerTitle>
            <DrawerDescription>A sequence number will be assigned automatically</DrawerDescription>
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-4 top-4"
              onClick={() => onOpenChange(false)}
              aria-label="Close drawer"
            >
              <X className="h-4 w-4" />
            </Button>
          </DrawerHeader>
          {renderContent()}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        side="right"
        className="w-[400px] sm:w-[540px] bg-background/95 backdrop-blur-sm border-l"
        onPointerDownOutside={e => e.preventDefault()}
        onInteractOutside={e => e.preventDefault()}
      >
        <SheetHeader>
          <SheetTitle>Create New Load</SheetTitle>
          <SheetDescription>A sequence number will be assigned automatically</SheetDescription>
        </SheetHeader>
        {renderContent()}
      </SheetContent>
    </Sheet>
  );
}
